import { EntityHandler, EntityListResult, SectionHandlerGroup, FormSectionHandler } from '../entity.handler';
import { IKuiTableCol } from 'app/key-ui';
import { Injectable } from '@angular/core';
import { ZoneResponse, ZoneCreateRequest, ZoneUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { GridColumnHelper, EntityUtils, EnumToComboValues } from '../utils';
import { ZoneDetailsSectionHandler } from './details.section';
import { ICONS } from '../icons';
import { RegisterEntityHandler } from '../../entities';
import { AssetGroupingService, MeasurementUnitsService } from 'app/services';
import { ZoneMapSectionHandler } from './map.section';
import { ZoneCoordsSectionHandler } from './coords.section';

@RegisterEntityHandler('zone')
@Injectable()
export class ZoneHandler extends EntityHandler<ZoneResponse> {

    constructor(
        admin: AdminEditorService,
        private grouping: AssetGroupingService,
        private units: MeasurementUnitsService
    ) {
        super(admin);
    }

    states = ['inactive', 'active', 'deleted'];

    listEntities(ownerId: string, offset: number, limit: number, sort: string, filter: string): Promise<EntityListResult> {
        return this.admin.getCached(`listZones/${ownerId}/${offset}/${limit}/${sort}/${filter}`, api => api.entities.listZones(ownerId, offset, limit, sort, filter), 3).then(response => ({
            ...response,
            type: 'zone',
            title: this.admin.i18n.instant('ADMIN.ENTITIES.ZONES'),
        }));
    }

    getEntity(id: string): Promise<ZoneResponse> {
        return this.admin.getCached(`getZone/${id}`, api => api.entities.getZone(id), 10);
    }

    createEntity(ownerId: string, entity: ZoneCreateRequest): Promise<ZoneResponse> {
        entity.ownerId = ownerId;
        return this.admin.applyUpdate(api => api.entities.createZone(entity));
    }

    setEntityState(entity: any, state: string): Promise<any> {
        return this.admin.applyUpdate(api => api.entities.updateZone(entity.id, { state: state as any }), { _type: 'zone' });
    }   

    getGridColumns(ownerId: string): Promise<IKuiTableCol[]> {
        return Promise.all([
            this.grouping.getZoneGroups(ownerId),
            this.grouping.getCostCentres(ownerId),
        ]).then(([zoneGroups, costCentres]) => {

            const grid = new GridColumnHelper(this.admin.i18n, 'ADMIN.EDITORS.ZONE.COLUMNS');
            const columns: IKuiTableCol[] = [
                grid.IconTextColumn('name', 'NAME', ICONS.zone),
                grid.ComboColumn('zoneType', 'TYPE', EnumToComboValues(this.admin.i18n, 'SHARED.ZONE_TYPES', ['location', 'keepin', 'nogo', 'route']), 80),
                grid.IdNameComboColumn('costCentre', 'COST_CENTRE', EntityUtils.treeToComboFilter(costCentres), 150),
                grid.IdNameComboColumn('group', 'GROUP', EntityUtils.treeToComboFilter(zoneGroups), 150),
                grid.ComboColumn('state', 'STATE', EnumToComboValues(this.admin.i18n, 'ADMIN.STATES', this.states, ['deleted']), 80),
                grid.TimeAgoColumn('modifiedDate', 'MODIFIED'),
            ];
            return columns;
        });
    }

    getEditorSections(ownerId: string, _entity: any): Promise<SectionHandlerGroup<ZoneResponse>[]> {
        return Promise.resolve([{
            name: this.admin.i18n.instant('ADMIN.EDITOR.INFORMATION'),
            sections: [
                new ZoneDetailsSectionHandler(this.admin, this.grouping, this.units, ownerId, false),
                new ZoneMapSectionHandler(this.admin, ownerId),
            ],
        }]);
    }

    getCreationSections(ownerId: string): Promise<{ entityType: string, entity: ZoneUpdateRequest, sections: FormSectionHandler<ZoneResponse, any>[] }> {
        return Promise.resolve({
            entityType: 'zone',
            entity: {}, // set up some defaults if you want
            sections: [
                new ZoneDetailsSectionHandler(this.admin, this.grouping, this.units, ownerId, true),
                new ZoneCoordsSectionHandler(this.admin, this.units, ownerId),
            ],
        });
    }

}












